/* eslint-disable no-shadow */
/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { WebViewerWrapper } from 'pdftron';
import AnnotationMixin from 'pdftron/docManager/Annotation';
import store, { requestsStore } from 'store/store';
import {
  AnnotationCustomData,
  Difference,
  DocumentTypes,
  GraphicZoneCustomData,
  InputAnnotation,
  InspectionStatuses,
  Match,
  PDFTronTools,
  DifferenceTypeNames,
  CTFModuleTypes,
} from 'types';
import {
  getNextGraphicZoneId,
  getSelectedTool,
  inspection,
  getFocusedDifference,
  getAutoMatchGraphic,
  getShowLiveText,
  getPagesImagesPath,
  app,
  getShowDifferenceBoxes,
  getIsStepAndRepeat,
  getRepeatHasDifferences,
  getSelectedModule,
  getShowRepeats,
  getGraphicMode,
  getGrayscaleMode,
  getImagesPathPrefix,
} from 'store';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import ToolsMixin from '../Tools';
import { shakeDocumentsZones } from './AnnotationTools';
import drawMarkupLabel from './utils/drawMarkupLabel';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import { PREP_TOOL_LABEL, PREP_TOOL_ANNOTATION } from './utils/annotationConstants';
import DifferenceClass from '../Difference';
import getPositionOfAnnotationRelativeToDocument from './utils/getAnnotationPositionRelativeToDocument';
import { fetchMatchInspection, startMatching } from '../../../store/request/match/actions';
import { CtfMatchLocation, CtfMatchResult } from '../../../store/request/match/types';
import Utils from 'components/PDFViewer/Utils';
import { FlashAnimationManager, AnimationConfig } from './utils/FlashAnimationManager';
import { getPageRotation, calculateRotatedLabelPosition, drawCounterRotatedLabel } from './utils/labelPositionUtils';
import { createAnnotationPopupButton } from './utils/createAnnotationPopupButton';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { MarkupMode } from './MarkupZone';

export interface SerializedGraphicAnnotation {
  pageNumber: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  shifted: boolean;
  fullPage?: boolean;
  scaled: boolean;
  matchedGraphic: boolean;
  autoGraphic: boolean;
  rotationIndex?: number;
}

declare global {
  interface Window {
    graphicAnimationInterval: any;
    markupAnimationInterval: any;
    currentShownDocument: DocumentTypes;
    requestInterval: (fn: any, delay: number) => void;
    clearRequestInterval: (handle: { value?: any }) => void;
  }
}

class GraphicZone {
  // This will be used to track the last selected graphic zone.
  static animatedZone = '';
  // Track the active animation mode to prevent race conditions when switching modes quickly
  static activeAnimationMode: string = '';

  source: WebViewerWrapper | null = null;

  target: WebViewerWrapper | null = null;

  declare markupMode: MarkupMode;
  declare originalMarkupId: string | null;
  declare comparisonSourceDocType: DocumentTypes | null;

  // currently, whether the file dimensions are equal is only relevant to graphic zones, but if this changes this could be moved to the store
  areFileDimensionsEqual = false;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;

    window.requestInterval = function (fn, delay) {
      let start = new Date().getTime();
      const handle: { value?: any } = {};

      function loop() {
        const current = new Date().getTime();
        const delta = current - start;

        if (delta >= delay) {
          fn.call();
          start = new Date().getTime();
        }

        handle.value = requestAnimationFrame(loop);
      }

      handle.value = requestAnimationFrame(loop);
      return handle;
    };

    window.clearRequestInterval = function (handle: { value?: any }) {
      window.cancelAnimationFrame(handle.value);
    };
  }

  get toolsMixin() {
    return new ToolsMixin(this.source, this.target);
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  createGraphicAnnotation(instance: WebViewerInstance, preloadZoneNumber?: number) {
    class GraphicZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        const state = store.getState();
        this.setCustomData(GraphicZoneCustomData.graphicAnnotation, 'true');
        this.setCustomData(GraphicZoneCustomData.zoneNumber, `${preloadZoneNumber || getNextGraphicZoneId(state)}`);
        this.setCustomData(GraphicZoneCustomData.confirmed, 'false');
        this.ToolName = PDFTronTools.GRAPHIC;
        this.IsHoverable = true;
        this.disableRotationControl();
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        this.setStyles(ctx, pageMatrix);
        const showDifferenceBoxes = getShowDifferenceBoxes(store.getState());

        const { DEFAULT_FILL_STYLE, SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;
        // Draw annotation rectangle with custom styling
        const annotationRectangle = new Path2D();
        annotationRectangle.rect(this.X, this.Y, this.Width, this.Height);
        ctx.strokeStyle = showDifferenceBoxes ? SELECTED_FILL_STYLE : 'rgba(255, 255, 255, 0.01)'; // transparent but clickable
        ctx.stroke(annotationRectangle);

        // Check to see if we are creating a graphic diff or zone.
        const state = store.getState();
        if (state.inspection.focusedDifferenceId === '-1') {
          // Draw differently when the annotation is confirmed
          if (this.getCustomData(GraphicZoneCustomData.confirmed) === 'true') {
            ctx.fillStyle = 'rgba(234, 54, 50, 0.23)';
            ctx.fill(annotationRectangle);
          } else {
            // This will allow the user to select unconfirmed graphic zones.
            ctx.fillStyle = 'rgba(255, 255, 255, 0.01)';
            ctx.fill(annotationRectangle);
          }

          const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
          const isSelected = selectedAnnotations.some((targetAnnot) => targetAnnot.Id.includes(this.Id));
          if (isSelected || (this as any)?.IsHovering) {
            const realPageRotation = getPageRotation(instance, this.PageNumber);
            const zoneNumber = this.getCustomData(GraphicZoneCustomData.zoneNumber);
            const labelText = `Graphic Zone: ${zoneNumber}`;
            const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
            const fillStyle = isSelected ? SELECTED_FILL_STYLE : DEFAULT_FILL_STYLE;
            const pageWidth = instance.Core.documentViewer.getPageWidth(this.PageNumber);
            const pageHeight = instance.Core.documentViewer.getPageHeight(this.PageNumber);
            drawMarkupLabel(
              ctx,
              labelText,
              this.X,
              this.Y,
              HEIGHT,
              GAP,
              WIDTH_PADDING,
              fillStyle,
              zoomLevel,
              false,
              this.Width,
              this.Height,
              realPageRotation,
              pageWidth,
              pageHeight,
            );
          }

          // For debug purposes
          // ctx.strokeStyle = '#000000';
          // ctx.strokeText(this.Id, this.Width / 2, this.Height / 2);
        }
      }
    }
    return GraphicZoneAnnotation;
  }

  createGraphicZoneAnnotationTools() {
    if (this.source && this.target) {
      [this.source, this.target].forEach((wrapper: WebViewerWrapper) => {
        const { instance } = wrapper;
        const toolObject = new (class GraphicCreateTool extends instance.Core.Tools.RectangleCreateTool {
          constructor(graphicAnnotation: any) {
            super(instance.Core.documentViewer);
            instance.Core.Tools.GenericAnnotationCreateTool.call(this, instance.Core.documentViewer, graphicAnnotation);
          }
        })(this.createGraphicAnnotation(instance));

        wrapper.registerTool({
          toolName: PDFTronTools.GRAPHIC,
          toolObject,
          buttonImage: '',
        });
      });
    }
  }

  getUnconfirmedGraphicZones() {
    return (
      this.source
        ?.getAnnotationList()
        .filter(
          (zone: Core.Annotations.Annotation) =>
            zone.getCustomData(GraphicZoneCustomData.confirmed) !== 'true' &&
            zone.getCustomData(GraphicZoneCustomData.graphicAnnotation) === 'true',
        ) || []
    );
  }

  getUnconfirmedTargetGraphicZones() {
    return (
      this.target
        ?.getAnnotationList()
        .filter(
          (zone: Core.Annotations.Annotation) =>
            zone.getCustomData(GraphicZoneCustomData.confirmed) !== 'true' &&
            zone.getCustomData(GraphicZoneCustomData.graphicAnnotation) === 'true',
        ) || []
    );
  }

  checkUnconfirmedGraphicZones() {
    const srouceUnconfirmed = this.getUnconfirmedGraphicZones();
    const targetUnconfirmed = this.getUnconfirmedTargetGraphicZones();
    return srouceUnconfirmed.length >= 1 || targetUnconfirmed.length >= 1;
  }

  selectUnconfirmedGraphicZone() {
    const sourceToolMode = this.source?.docViewer.getToolMode();
    const targetToolMode = this.target?.docViewer.getToolMode();

    // don't select during shifted graphics
    if (
      sourceToolMode?.name === PDFTronTools.SHIFTED_GRAPHIC ||
      targetToolMode?.name === PDFTronTools.SHIFTED_GRAPHIC
    ) {
      return;
    }

    if (targetToolMode?.name === PDFTronTools.SCALED_GRAPHIC) {
      const targetShiftedAnnot = this.target?.getAnnotationList(
        (annot: any) => annot.ToolName === PDFTronTools.SCALED_GRAPHIC,
      )[0];

      if (targetShiftedAnnot) {
        this.target?.jumpToAnnotation(targetShiftedAnnot.Id);
        this.target?.annotationManager.selectAnnotation(targetShiftedAnnot);
      }
    } else {
      this.source?.annotationManager.deselectAllAnnotations();
      const sourceUnconfirmed = this.getUnconfirmedGraphicZones()[0];
      if (sourceUnconfirmed) {
        this.source?.jumpToAnnotation(sourceUnconfirmed.Id);
        this.source?.annotationManager.selectAnnotation(sourceUnconfirmed);
      }
      const targetUnconfirmed = this.getUnconfirmedTargetGraphicZones()[0];
      if (targetUnconfirmed) {
        this.target?.jumpToAnnotation(targetUnconfirmed.Id);
        this.target?.annotationManager.selectAnnotation(targetUnconfirmed);
      }
    }
  }

  private deleteGraphicZones() {
    const annotationToDelete = this.source?.annotationManager.getSelectedAnnotations()[0];
    const annotationToDeleteTarget = this.target?.annotationManager.getSelectedAnnotations()[0];
    if (annotationToDelete?.getCustomData(GraphicZoneCustomData.graphicAnnotation) === 'true') {
      this.source?.annotationManager.deleteAnnotation(annotationToDelete, { imported: true, force: true });
      if (annotationToDeleteTarget) {
        this.target?.annotationManager.deleteAnnotation(annotationToDeleteTarget, { imported: true, force: true });
      }

      // Updates annotations in redux if they were added in the first place.
      if (
        annotationToDelete.getCustomData(GraphicZoneCustomData.confirmed) === 'true' &&
        annotationToDeleteTarget?.getCustomData(GraphicZoneCustomData.confirmed) === 'true'
      ) {
        this.annotationMixin.updateInputAnnotations(); // as this function is also called on the unconfirmed error, we need to prevent autosave.
      }
      shakeDocumentsZones(annotationToDelete);
    }
  }

  handlingDocumentHighlight() {
    const sourceUnconfirmed = this.getUnconfirmedGraphicZones();
    const targetUnconfirmed = this.getUnconfirmedTargetGraphicZones();
    const selectedTool = getSelectedTool(store.getState());

    if (selectedTool === PDFTronTools.GRAPHIC) {
      // if nothing is confirmed at all (Or both confirmed al-ready)
      if (!this.checkUnconfirmedGraphicZones()) {
        this.target?.removeAnnotationDocumentHighlight();
        this.source?.addDocumentHighlight();
      }

      // if target not confirmed yet
      if (sourceUnconfirmed.length === 0 && targetUnconfirmed.length >= 1) {
        this.source?.removeAnnotationDocumentHighlight();
        this.target?.addDocumentHighlight();
      }
    }
  }

  getSerializedGraphicZones(documentType: DocumentTypes = DocumentTypes.source): SerializedGraphicAnnotation[] {
    // please someone make this nicer
    const annotationManager = this[documentType]?.annotationManager;

    if (annotationManager) {
      const graphicZoneAnnotations = annotationManager
        .getAnnotationsList()
        .filter(
          (annotation) =>
            annotation.getCustomData(GraphicZoneCustomData.graphicAnnotation) &&
            annotation.getCustomData(GraphicZoneCustomData.confirmed),
        );
      return (
        graphicZoneAnnotations?.map((annotation) => {
          const { x1, x2, y1, y2 } = annotation.getRect();
          const pageHeight = this[documentType]?.docViewer.getPageHeight(annotation.PageNumber);
          return {
            pageNumber: annotation.PageNumber,
            pageHeight,
            x1,
            x2,
            y1,
            y2,
            shifted: annotation.getCustomData(GraphicZoneCustomData.shifted) === 'true',
            scaled: annotation.getCustomData(GraphicZoneCustomData.scaled) === 'true',
            autoGraphic: annotation.getCustomData(GraphicZoneCustomData.autoGraphicsAnnotations) === 'true',
            matchedGraphic: annotation.getCustomData(GraphicZoneCustomData.matchedGraphicsAnnotation) === 'true',
            rotationIndex:
              documentType === DocumentTypes.target
                ? parseInt(annotation.getCustomData(GraphicZoneCustomData.rotationIndex) || '0', 10)
                : undefined,
          };
        }) || []
      );
    }
    return [];
  }

  /**
   * update whether source and target file are equal in dimensions and number of pages
   */
  updateAreFileDimensionsEqual() {
    if (this.source && this.target) {
      const sourceTotal = this.source.page.getTotalPages();
      const targetTotal = this.target.page.getTotalPages();
      const sourceTotalSize = this.source.page.getTotalPageSize();
      const targetTotalSize = this.target.page.getTotalPageSize();
      if (sourceTotal !== targetTotal) {
        this.areFileDimensionsEqual = false;
      } else {
        this.areFileDimensionsEqual = sourceTotalSize.every(
          (pageSize, index) =>
            pageSize.height === targetTotalSize[index].height && pageSize.width === targetTotalSize[index].width,
        );
      }
    }
  }

  /**
   * Duplicate graphic zone to selected source/target instance
   * @param zone Annotation to be duplicated
   * @param instance The selected destination for the duplicated annotation to display
   */
  duplicateGraphicZone(zone: Core.Annotations.Annotation, instance: WebViewerInstance, page?: number) {
    const annotationManager = instance.Core.documentViewer.getAnnotationManager();
    // Creating target zone annotation with the same values as in source
    const targetZone = new (this.createGraphicAnnotation(
      instance,
      parseInt(zone.getCustomData(GraphicZoneCustomData.zoneNumber), 10),
    ))();
    targetZone.ReadOnly = true;
    targetZone.setCustomData(GraphicZoneCustomData.confirmed, 'false');
    targetZone.Width = zone.Width;
    targetZone.Height = zone.Height;
    targetZone.Id = zone.Id;
    // if Source and New files don't have the same dimensions and number of pages, the New graphics zone is created on the top left corner of the first page
    targetZone.X = this.areFileDimensionsEqual ? zone.X : 0;
    targetZone.Y = this.areFileDimensionsEqual ? zone.Y : 0;
    targetZone.PageNumber = page || Math.min(zone.PageNumber, instance.Core.documentViewer.getPageCount());

    annotationManager.addAnnotation(targetZone);
    annotationManager.redrawAnnotation(targetZone);
    annotationManager.selectAnnotation(targetZone);
  }

  /**
   * Duplicate auto-matched graphic zone to target
   * @param zone Annotation to be duplicated
   * @param instance The target instance
   * @param ctfBestMatch The result for finding a graphic zone
   */
  duplicateFoundGraphicZone(
    zone: Core.Annotations.Annotation,
    instance: WebViewerInstance,
    ctfBestMatch: CtfMatchLocation,
  ) {
    const annotationManager = instance.Core.documentViewer.getAnnotationManager();

    // Creating target zone annotation with the same values as in source
    const targetZone = new (this.createGraphicAnnotation(
      instance,
      parseInt(zone.getCustomData(GraphicZoneCustomData.zoneNumber), 10),
    ))();

    targetZone.ReadOnly = true;
    targetZone.setCustomData(GraphicZoneCustomData.confirmed, 'false');

    targetZone.setCustomData(GraphicZoneCustomData.shifted, 'true');
    targetZone.setCustomData(GraphicZoneCustomData.scaled, 'true');
    targetZone.setCustomData(GraphicZoneCustomData.autoGraphicsAnnotations, 'true');
    targetZone.setCustomData(GraphicZoneCustomData.matchedGraphicsAnnotation, 'true');

    if (ctfBestMatch.rotationIndex) {
      targetZone.setCustomData(GraphicZoneCustomData.rotationIndex, ctfBestMatch.rotationIndex.toString());
    }

    const sampleBoundingBox = ctfBestMatch.boundingBox;
    targetZone.Width = sampleBoundingBox.right - sampleBoundingBox.left;
    targetZone.Height = sampleBoundingBox.bottom - sampleBoundingBox.top;
    targetZone.Id = zone.Id;
    targetZone.X = sampleBoundingBox.left;
    targetZone.Y = sampleBoundingBox.top;
    targetZone.PageNumber = ctfBestMatch.pageNumber;

    annotationManager.addAnnotation(targetZone);
    annotationManager.redrawAnnotation(targetZone);
    annotationManager.selectAnnotation(targetZone);
    return targetZone;
  }

  animateLoader(iteration = 0) {
    const targetAnnoManager = this.target?.annotationManager;
    const loader = targetAnnoManager?.getAnnotationById('loader');
    const state = store.getState();
    const showDifferenceBoxes = getShowDifferenceBoxes(state);

    if (loader !== undefined && loader !== null && state.inspection.focusedDifferenceId !== '-1') {
      loader.draw = (ctx: CanvasRenderingContext2D) => {
        const x = loader.X;
        const y = loader.Y;
        const w = loader.Width;
        const h = loader.Height;

        if (x && y && w && h) {
          ctx.globalAlpha = 1;
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.globalCompositeOperation = 'source-over';
          ctx.fillStyle = `rgba(255,255,255, ${showDifferenceBoxes ? 0.9 : 0.01})`;
          ctx.fillRect(x, y, w, h);
          ctx.closePath();

          const minVal = Math.min(h, w);
          let diameter = 70;
          if (diameter >= minVal) {
            diameter = minVal - minVal * 0.09;
          }

          ctx.beginPath();
          ctx.strokeStyle = `rgba(255,0,0,1)`;
          ctx.lineWidth = Math.floor(diameter * 0.06);
          ctx.arc(
            x + w / 2,
            y + h / 2,
            diameter / 2,
            (Math.PI / 16) * iteration,
            Math.PI / 3 + (Math.PI / 16) * iteration,
            false,
          );
          ctx.stroke();
        }
      };
      targetAnnoManager?.redrawAnnotation(loader);

      setTimeout(() => {
        this.animateLoader(iteration + 1);
      }, 10);
    }
  }

  private drawFn(
    ctx: CanvasRenderingContext2D,
    imageBitMap: ImageBitmap | null,
    { x = 0, y = 0, width = 0, height = 0, rotationIndex = 0, matchIndex = 0, pageNumber = 1 },
    isSource = false,
    imageAlpha = 1,
  ) {
    if (!imageBitMap) return;

    const state = store.getState();
    const showDifferenceBoxes = getShowDifferenceBoxes(state);
    const isStepAndRepeat = getIsStepAndRepeat(state);
    const repeatHasDifferences = getRepeatHasDifferences(matchIndex)(state);
    const selectedModule = getSelectedModule(state);
    const showRepeats = getShowRepeats(state);
    const graphicMode = getGraphicMode(state);
    const grayscaleMode = getGrayscaleMode(state);

    // Get current page rotation dynamically at draw time so it updates when user rotates
    const pageRotation = this.target?.instance ? getPageRotation(this.target.instance, pageNumber) : 0;

    ctx.globalCompositeOperation = 'destination-over';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.save();

    let drawX: number = x;
    let drawY: number = y;
    let drawWidth: number = width;
    let drawHeight: number = height;

    if (isSource && rotationIndex) {
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate((rotationIndex * 90 * Math.PI) / 180);

      // Adjust for swapped dimensions in 90°/270° rotations
      drawX = rotationIndex % 2 !== 0 ? -height / 2 : -width / 2;
      drawY = rotationIndex % 2 !== 0 ? -width / 2 : -height / 2;
      drawWidth = rotationIndex % 2 !== 0 ? height : width;
      drawHeight = rotationIndex % 2 !== 0 ? width : height;
    }

    // Apply alpha for the image content
    ctx.globalAlpha = imageAlpha;
    if (grayscaleMode) {
      ctx.filter = 'grayscale(100%)';
    }
    ctx.drawImage(imageBitMap, drawX, drawY, drawWidth, drawHeight);
    if (grayscaleMode) {
      ctx.filter = 'none';
    }

    // Draw a solid white background to cover any transparency in the flashing image
    // Only draw white background in flashing mode, not in transparency overlay mode
    if (graphicMode !== 'transparentOverlay') {
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = 'white';
      ctx.fillRect(drawX, drawY, drawWidth, drawHeight);
    }
    ctx.restore();

    let strokeStyle; // color for outline
    let fillStyle; // color for label
    let label: string;

    if (isStepAndRepeat) {
      strokeStyle = repeatHasDifferences ? '#FF9800' : '#2ED2AB';
      fillStyle = repeatHasDifferences ? '#FF9800' : '#2ED2AB';
      label = isSource ? 'Source' : `Repeat ${matchIndex + 1}`;
    } else {
      strokeStyle = showDifferenceBoxes ? '#6DDAE2' : 'rgba(255, 255, 255, 0.01)';
      fillStyle = '#6DDAE2';
      label = isSource ? 'Source' : 'New';
    }

    if (!isStepAndRepeat || (isStepAndRepeat && selectedModule === CTFModuleTypes.graphics && showRepeats)) {
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = strokeStyle;
      const containerBorderWidth = 1.5;
      ctx.lineWidth = containerBorderWidth;
      ctx.strokeRect(x, y, width, height);

      ctx.font = '11px Inter, sans-serif';
      const iconSize = 12;
      const iconPadding = 2;
      const textPadding = 4;
      const labelHeight = 16;
      const labelBorderRadius = 2;

      const { width: textWidth } = ctx.measureText(label);
      const iconWidth = isStepAndRepeat ? iconSize + iconPadding : 0;
      // Labels render at their natural per-text width. The Source/New tail-overlap problem is
      // handled at the FlashAnimationManager layer by giving the animation annotations a
      // bounding rect that includes the label area, so PDFTron's per-annotation invalidation
      // clears the label pixels between frames. See FlashAnimationManager.startFlashAnimation.
      const labelWidth = textWidth + textPadding * 2 + iconWidth;

      // Calculate label anchor position based on page rotation to keep it in perceived top-left
      const bounds = {
        x: Math.min(x, x + width), // handle negative width
        y: Math.min(y, y + height), // handle negative height
        width: Math.abs(width),
        height: Math.abs(height),
      };

      const labelDimensions = { width: labelWidth, height: labelHeight };
      // All flashing labels (Source/New/Repeat) position outside the container so they do not obscure content
      const positionInside = false;
      const pgW = this.target?.instance ? this.target.instance.Core.documentViewer.getPageWidth(pageNumber) : undefined;
      const pgH = this.target?.instance
        ? this.target.instance.Core.documentViewer.getPageHeight(pageNumber)
        : undefined;
      const labelPosition = calculateRotatedLabelPosition(
        bounds,
        labelDimensions,
        pageRotation,
        0,
        positionInside,
        pgW,
        pgH,
      );

      // Draw the complete label with counter-rotation so it always appears horizontal.
      drawCounterRotatedLabel(
        ctx,
        label,
        labelPosition.x,
        labelPosition.y,
        labelWidth,
        labelHeight,
        pageRotation,
        fillStyle,
        textPadding,
        labelBorderRadius,
        isStepAndRepeat && showRepeats ? Utils.getRepeatIcon() : undefined,
        iconSize,
        iconPadding,
        '11px Inter, sans-serif',
        containerBorderWidth,
      );
    }
  }

  private extractDpiFromPath(imagePath: string | undefined): number | null {
    if (!imagePath) return null;

    const filename = imagePath.split('/').pop() || '';
    const dpiPattern = /^\d+_dpi\((\d+)\)\.png$/;
    const match = filename.match(dpiPattern);

    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Parses the 1-based page number encoded in CTF full-page PNG basenames (e.g. `7.png`,
   * `7_dpi(300).png`), matching backend `pageNumber` / `constructImagePath` in
   * apps/api inspection.service — not a 0-based index.
   */
  private getPageNumberFromPagesImageFilename(imagePath: string | undefined): number | null {
    if (!imagePath) return null;
    const filename = imagePath.split('/').pop() || '';
    const dpiFormat = /^(\d+)_dpi\(\d+\)\.png$/;
    const dpiMatch = filename.match(dpiFormat);
    if (dpiMatch) return parseInt(dpiMatch[1], 10);
    const simple = /^(\d+)\.png$/;
    const simpleMatch = filename.match(simple);
    if (simpleMatch) return parseInt(simpleMatch[1], 10);
    return null;
  }

  async animateMatch(match: Match) {
    if (!this.target || !this.source || !match) return;

    const state = store.getState();
    const graphicMode = getGraphicMode(state);
    if (!graphicMode) return;

    // Set these BEFORE any async operations to track which mode we're animating
    const animationId = match.index.toString();
    GraphicZone.animatedZone = animationId;
    GraphicZone.activeAnimationMode = graphicMode;

    this.source?.annotationManager.deselectAllAnnotations();
    this.target?.annotationManager.deselectAllAnnotations();

    const constructImagePath = (type: 'master' | 'sample', pageNumber: number) => {
      const basePath = getPagesImagesPath(type)(state);
      return `${basePath}${pageNumber}.png`;
    };

    const fetchImageBlob = FlashAnimationManager.fetchImageBlob;
    const matchDetail = match.matchDetails[0];

    let sourceImageBlob: Blob;
    let targetImageBlob: Blob | undefined;

    try {
      const imagesPathPrefix = getImagesPathPrefix();

      // Use backend paths only when they refer to the same page as *Location (e.g. user-linked
      // embedded-image matches update location but not masterImage/sampleImage).
      const masterImagePath = matchDetail.masterImage?.path;
      const masterPathPage = this.getPageNumberFromPagesImageFilename(masterImagePath);
      const useMasterPath = masterImagePath != null && masterPathPage === matchDetail.masterLocation.pageNumber;
      const sourceImagePath = useMasterPath
        ? `${imagesPathPrefix}${masterImagePath}`
        : constructImagePath('master', matchDetail.masterLocation.pageNumber);
      sourceImageBlob = await fetchImageBlob(sourceImagePath);

      // Check if animation was cancelled or mode changed during fetch
      if (GraphicZone.animatedZone !== animationId || GraphicZone.activeAnimationMode !== graphicMode) {
        return;
      }

      if (graphicMode !== 'transparentOverlay') {
        const sampleImagePath = matchDetail.sampleImage?.path;
        const samplePathPage = this.getPageNumberFromPagesImageFilename(sampleImagePath);
        const useSamplePath = sampleImagePath != null && samplePathPage === matchDetail.sampleLocation.pageNumber;
        const targetImagePath = useSamplePath
          ? `${imagesPathPrefix}${sampleImagePath}`
          : constructImagePath('sample', matchDetail.sampleLocation.pageNumber);
        targetImageBlob = await fetchImageBlob(targetImagePath);

        // Check again after second fetch
        if (GraphicZone.animatedZone !== animationId || GraphicZone.activeAnimationMode !== graphicMode) {
          return;
        }
      }
    } catch (error) {
      return; // Exit early if we can't fetch the images
    }

    // Determine the type of flash mode and the image to be used
    const FlashModeCFG: Record<string, any> = {
      ['Match']: async (_: WebViewerWrapper) => {
        const matchDetails = match.matchDetails[0];
        const sourceRight = matchDetails.masterLocation.boundingBox.right || 0;
        const sourceBottom = matchDetails.masterLocation.boundingBox.bottom || 0;
        const sourceLeft = matchDetails.masterLocation.boundingBox.left || 0;
        const sourceTop = matchDetails.masterLocation.boundingBox.top || 0;
        const sourceWidth = sourceRight - sourceLeft;
        const sourceHeight = sourceBottom - sourceTop;

        // Use DPI from backend if available, otherwise fallback to knownDPI
        const knownDPI = 300;
        const sourceDPI = matchDetail.masterImage?.dpiUsed || knownDPI;
        const sourceScaleFactor = sourceDPI / 72;

        const sourceImageBitMap = await createImageBitmap(
          sourceImageBlob,
          sourceLeft * sourceScaleFactor,
          sourceTop * sourceScaleFactor,
          sourceWidth * sourceScaleFactor,
          sourceHeight * sourceScaleFactor,
        );

        const targetRight = matchDetails.sampleLocation.boundingBox.right || 0;
        const targetBottom = matchDetails.sampleLocation.boundingBox.bottom || 0;
        const targetLeft = matchDetails.sampleLocation.boundingBox.left || 0;
        const targetTop = matchDetails.sampleLocation.boundingBox.top || 0;
        const targetWidth = targetRight - targetLeft;
        const targetHeight = targetBottom - targetTop;

        // Use DPI from backend if available, otherwise fallback to knownDPI
        const targetDPI = matchDetail.sampleImage?.dpiUsed || knownDPI;
        const targetScaleFactor = targetDPI / 72;

        const targetImageBitMap =
          graphicMode !== 'transparentOverlay'
            ? await createImageBitmap(
                targetImageBlob as Blob,
                targetLeft * targetScaleFactor,
                targetTop * targetScaleFactor,
                targetWidth * targetScaleFactor,
                targetHeight * targetScaleFactor,
              )
            : null;

        return {
          width: targetWidth,
          height: targetHeight,
          x: targetLeft,
          y: targetTop,
          sourceImageBitMap,
          targetImageBitMap,
        };
      },
    };

    const { width, height, x, y, sourceImageBitMap, targetImageBitMap } = await FlashModeCFG['Match'](this.target);

    // Final check before starting animation - mode/zone might have changed during image processing
    if (GraphicZone.animatedZone !== animationId || GraphicZone.activeAnimationMode !== graphicMode) {
      return;
    }

    // Use FlashAnimationManager for the animation
    const config: AnimationConfig = {
      pageNumber: matchDetail.sampleLocation.pageNumber,
      x,
      y,
      width,
      height,
      sourceImageBitmap: sourceImageBitMap as ImageBitmap,
      targetImageBitmap: targetImageBitMap,
      animationId: match.index.toString(),
      intervalName: 'graphicAnimationInterval',
      annotationSubject: 'GraphicAnnotationAnimated',
      drawFn: (ctx, imageBitmap, details, isSource, alpha = 1) => {
        const annotationDetails = {
          ...details,
          rotationIndex: match.matchDetails[0].sampleLocation.rotationIndex,
          matchIndex: match.index,
          pageNumber: matchDetail.sampleLocation.pageNumber, // Pass page number for dynamic rotation lookup
        };
        this.drawFn(ctx, imageBitmap, annotationDetails, isSource, alpha);
      },
      onBeforeAnimate: () => {
        // Handle step-and-repeat hiding
        const isStepAndRepeat = getIsStepAndRepeat(state);
        if (isStepAndRepeat && getShowRepeats(state)) {
          const repeatAnnotation = this.target?.annotationManager.getAnnotationById(`repeat-${match.index}`);
          if (repeatAnnotation) {
            repeatAnnotation.Hidden = true;
          }
        }
      },
      graphicMode,
    };

    await FlashAnimationManager.startFlashAnimation(this.target, config, () => GraphicZone.animatedZone);

    // if live text is enabled when the difference is selected, we must redraw the text highlight
    const showLiveText = getShowLiveText(store.getState());
    if (showLiveText) {
      PDFTronManager.redrawLiveTextHighlights();
    }
  }

  reloadGraphicZones(sourceAnnotationsData: InputAnnotation[], targetAnnotationsData: InputAnnotation[]) {
    const getGraphicAnnotationObject = (
      inputAnnotation: InputAnnotation,
      instance: WebViewerInstance,
      index: number,
    ) => {
      const GraphicAnnotation = this.createGraphicAnnotation(instance, index + 1);
      const annotation = new GraphicAnnotation();
      annotation.PageNumber = inputAnnotation.page;
      annotation.setCustomData(GraphicZoneCustomData.confirmed, 'true');
      annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');
      annotation.Id = inputAnnotation.annotationId;
      if (inputAnnotation.graphicZone) {
        const { x1, x2, y1, y2 } = inputAnnotation.graphicZone;
        annotation.X = x1;
        annotation.Y = y1;
        annotation.Width = Math.abs(x2 - x1);
        annotation.Height = Math.abs(y2 - y1);
        annotation.ReadOnly = true;
        if (inputAnnotation.graphicZone.shifted) {
          annotation.setCustomData(GraphicZoneCustomData.shifted, 'true');
        } else if (inputAnnotation.graphicZone.scaled) {
          annotation.setCustomData(GraphicZoneCustomData.scaled, 'true');
        } else if (inputAnnotation.graphicZone.autoGraphic) {
          annotation.setCustomData(GraphicZoneCustomData.autoGraphicsAnnotations, 'true');
        } else if (inputAnnotation.graphicZone.matchedGraphic) {
          annotation.setCustomData(GraphicZoneCustomData.matchedGraphicsAnnotation, 'true');
        }

        if (inputAnnotation.graphicZone.rotationIndex) {
          annotation.setCustomData(
            GraphicZoneCustomData.matchedGraphicsAnnotation,
            inputAnnotation.graphicZone.rotationIndex.toString(),
          );
        }
      }
      return annotation;
    };
    sourceAnnotationsData.forEach((inputAnnotation, index) => {
      if (!this.source || !inputAnnotation.graphicZone) return;
      const { instance } = this.source;
      if (instance && !inputAnnotation.graphicZone.fullPage) {
        const annotObject = getGraphicAnnotationObject(inputAnnotation, instance, index);
        this.source.annotationManager.addAnnotation(annotObject);
        this.source.annotationManager.drawAnnotations({ pageNumber: annotObject.PageNumber });
      }
    });
    targetAnnotationsData.forEach((inputAnnotation, index) => {
      if (!this.target || !inputAnnotation.graphicZone) return;
      const { instance } = this.target;
      if (instance && !inputAnnotation.graphicZone.fullPage) {
        const annotObject = getGraphicAnnotationObject(inputAnnotation, instance, index);
        this.target.annotationManager.addAnnotation(annotObject);
        this.target.annotationManager.drawAnnotations({ pageNumber: annotObject.PageNumber });
      }
    });
    store.dispatch(inspection.actions.setGraphicZoneId(sourceAnnotationsData.length + 1));
  }

  loadCanvas(
    instance: WebViewerWrapper,
    annotation: Core.Annotations.Annotation,
    zoom: number,
    documentRotation: number,
    matchRotation: number,
  ): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      // annotation is always using the upright orientation, so we need to correct it to its document rotation before it is rotated
      const unrotatedRect = annotation.getRect();
      unrotatedRect.x1;
      const correctedRender = this.correctRenderRectByRotation(
        instance,
        unrotatedRect,
        annotation.getPageNumber(),
        documentRotation,
      );
      const pageNumber = annotation.getPageNumber();
      const uprightCorrection = (4 - documentRotation + matchRotation) % 4; // we need to complete the rotation depending on so that the canvas is upright (ex. if 90deg, we need to rotate 270 more so that it is 0)
      const doc = instance.docViewer.getDocument();
      doc.loadCanvas({
        drawComplete: (canvasOrImage: HTMLCanvasElement | HTMLImageElement, pageNumber: number) => {
          // Use duck typing instead of instanceof due to cross-realm (iframe) issues
          if ('getContext' in canvasOrImage && typeof canvasOrImage.getContext === 'function') {
            resolve(canvasOrImage as HTMLCanvasElement);
            return;
          }

          // If it's an image, draw it onto a canvas first
          const canvas = document.createElement('canvas');
          canvas.width = canvasOrImage.width;
          canvas.height = canvasOrImage.height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not create canvas context for image'));
            return;
          }

          ctx.drawImage(canvasOrImage, 0, 0);
          resolve(canvas);
        },
        pageNumber,
        zoom,
        renderRect: correctedRender,
        pageRotation: uprightCorrection,
      });
    });
  }

  private correctRenderRectByRotation(
    instance: WebViewerWrapper,
    rect: Core.Math.Rect,
    pageNumber: number,
    documentRotation: number,
  ): { x1: number; y1: number; x2: number; y2: number } {
    const height = instance.docViewer.getPageHeight(pageNumber);
    const width = instance.docViewer.getPageWidth(pageNumber);

    let { x1, y1, x2, y2 } = rect;

    switch (documentRotation) {
      // 90 degrees
      case 1:
        [x1, y1, x2, y2] = [height - y2, x1, height - y1, x2];
        break;
      // 180 degrees
      case 2:
        [x1, y1, x2, y2] = [width - x2, height - y2, width - x1, height - y1];
        break;
      // 270 degrees
      case 3:
        [x1, y1, x2, y2] = [y1, width - x2, y2, width - x1];
        break;
    }

    return { x1, y1, x2, y2 };
  }

  createLoadingAnnotation(difference: Difference): Core.Annotations.RectangleAnnotation | null {
    if (!this.target) return null;
    const showDifferenceBoxes = getShowDifferenceBoxes(store.getState());
    const loader = new (DifferenceClass.createDifferenceAnnotation(this.target.instance, DocumentTypes.target))({
      annotData: {
        transformCoord: difference.transformCoord || false,
        location: {
          rect: difference.target.location.rect,
          pageNumber: difference.target.location.pageNumber,
        },
        text: difference.target.text,
      },
      differenceId: 'loader',
      type: difference.type,
    });

    if (loader === null) return null;
    loader.Id = 'loader';
    loader.draw = (ctx: CanvasRenderingContext2D) => {
      ctx.globalAlpha = 1;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = `rbga(255,255,255, ${showDifferenceBoxes ? 1 : 0.01})`;
      ctx.fillRect(loader.X, loader.Y, loader.Width, loader.Height);
      ctx.closePath();
    };

    return loader;
  }

  resetAnimation() {
    // Routed through stopAnimation so the per-animation rotationUpdated listener is torn down.
    FlashAnimationManager.stopAnimation(
      'graphicAnimationInterval',
      'GraphicAnnotationAnimated',
      this.source,
      this.target,
    );
    FlashAnimationManager.stopAnimation(
      'graphicAnimationInterval',
      'GraphicAnnotationOverlay',
      this.source,
      this.target,
    );

    this.target?.annotationManager.deleteAnnotations(
      this.target.getAnnotationList().filter((annot: Core.Annotations.Annotation) => annot.Id === 'loader'),
      { imported: false, force: true },
    );

    const state = store.getState();
    const isStepAndRepeat = getIsStepAndRepeat(state);
    if (isStepAndRepeat) {
      const selectedModule = getSelectedModule(state);
      const showRepeats = getShowRepeats(state);
      if (GraphicZone.animatedZone !== '' && selectedModule === CTFModuleTypes.graphics && showRepeats) {
        const repeatAnnotation = this.target?.annotationManager.getAnnotationById(`repeat-${GraphicZone.animatedZone}`);
        if (repeatAnnotation) {
          repeatAnnotation.Hidden = false;
        }
      }
    }

    GraphicZone.animatedZone = '';
    // Only clear activeAnimationMode when graphics mode is turned off completely
    const currentMode = getGraphicMode(state);
    if (!currentMode) {
      GraphicZone.activeAnimationMode = '';
    }
  }

  hasConfirmedGraphicZones() {
    if (!this.source) return false;
    return this.source.annotationManager
      .getAnnotationsList()
      .some(
        (annot) =>
          annot.getCustomData(GraphicZoneCustomData.graphicAnnotation) === 'true' &&
          annot.getCustomData(GraphicZoneCustomData.confirmed) === 'true',
      );
  }

  getSeparator() {
    const renderSeparator = () => {
      const separator = document.createElement('div');
      separator.innerText = '|';
      separator.style.color = '#8892AB';
      separator.style.position = 'relative';
      separator.style.alignItems = 'center';
      separator.style.display = 'block';
      separator.style.justifyContent = 'center';
      separator.style.width = 'fit-content';
      separator.style.padding = '6px';
      return separator;
    };
    return {
      type: 'customElement',
      render: renderSeparator,
    };
  }
  // Related to - VE-2801 - Graphic Zone Selection (To Finish)
  getEditShiftedPopup(instance: WebViewerInstance, DropDownMenuDirection: string) {
    const renderShiftButton = () => {
      const button = document.createElement('button');
      button.classList.add('GraphicsZoneDropdown', 'ActionButton');
      button.style.width = 'fit-content';
      button.style.display = 'block';
      button.style.position = 'relative';
      button.style.justifyContent = 'center';
      button.style.alignItems = 'center';
      button.style.paddingLeft = '6px';
      button.style.paddingRight = '8px';
      button.style.height = '32px';

      const icon = document.createElement('img');
      icon.src = '/icons/shiftedGraphics.svg';
      icon.style.width = '24px';
      icon.style.height = '24px';
      icon.style.display = 'block';
      icon.style.alignItems = 'center';
      icon.style.position = 'relative';
      icon.style.justifyContent = 'center';
      button.appendChild(icon);

      const htmlmenu = document.createElement('div');
      htmlmenu.style.display = 'none';
      htmlmenu.style.position = 'relative';
      htmlmenu.style.backgroundColor = '#21242A';
      htmlmenu.style.width = '130px';
      htmlmenu.style.borderRadius = '4px';
      htmlmenu.style.marginTop = DropDownMenuDirection;
      htmlmenu.style.marginLeft = '-10px';
      htmlmenu.style.flexDirection = 'column';
      htmlmenu.style.padding = '4px';
      htmlmenu.style.zIndex = '1';
      const htmlItems = `
      <style>
      @media screen and (max-width: 1500px) {
        .buttonClass{
          display: inline-block;
          position: relative;
          justify-content: center;
          align-items: center;
          padding: 4px;
          text-transform: capitalize;
          font-size: 12px;
          letter-spacing: 0;
          line-height: 16px;
          border: none;
          background-color: transparent;
          cursor: pointer;
          border-radius: 4px;
          width: auto;
          height: 24px;
          outline: none;
          white-space: nowrap;
        }
      }
      .buttonClass:hover{
        cursor: pointer;
        background-color: rgba(51, 66, 80, 1);
        position: relative;
      }
      .buttonClass:focus {
        background-color: rgba(255, 255, 255, 0.08);
        outline: none;
      }
      </style>`;
      htmlmenu.innerHTML = htmlItems;
      button.appendChild(htmlmenu);

      const ShiftOnly = document.createElement('button');
      ShiftOnly.classList.add('buttonClass');
      ShiftOnly.innerText = 'Shifted Graphics';
      htmlmenu.appendChild(ShiftOnly);

      const ShiftScaled = document.createElement('button');
      ShiftScaled.classList.add('buttonClass');
      ShiftScaled.innerText = 'Scaled Graphics';
      htmlmenu.appendChild(ShiftScaled);

      let isPopUpOpen = false;
      const closePopUp = () => {
        htmlmenu.style.display = 'none';
        isPopUpOpen = false;
      };

      button.addEventListener('click', () => {
        htmlmenu.style.display = 'block';

        if (isPopUpOpen) {
          closePopUp();
          button.classList.remove('open');
        } else {
          htmlmenu.style.display = 'block';
          isPopUpOpen = true;
          button.classList.add('open');
        }
      });

      ShiftOnly?.addEventListener('click', () => {
        const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
        if (selectedAnnotations.length === 1) {
          // represents the selected target graphic zone
          const selectedGraphicZone = selectedAnnotations[0];
          // set the selected source and target graphic zone Ids as current shiftedGraphicRefId in redux
          store.dispatch(inspection.actions.setShiftedGraphicRefId(selectedGraphicZone.Id));
          this.source?.instance.UI.setToolMode(PDFTronTools.SHIFTED_GRAPHIC);
          closePopUp();
        }
      });

      ShiftScaled?.addEventListener('click', () => {
        const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
        if (selectedAnnotations.length === 1) {
          // represents the selected target graphic zone
          const selectedGraphicZone = selectedAnnotations[0];
          // set the selected source and target graphic zone Ids as current shiftedGraphicRefId in redux
          store.dispatch(inspection.actions.setShiftedGraphicRefId(selectedGraphicZone.Id));
          this.target?.instance.UI.setToolMode(PDFTronTools.SCALED_GRAPHIC);
          this.target?.addDocumentHighlight();
          closePopUp();
        }
      });

      return button;
    };

    return {
      type: 'customElement',
      render: renderShiftButton,
    };
  }

  graphicZoneAnnotationSelected(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, _action: string) {
    if (documentType === DocumentTypes.source && this.source) {
      const popupButtons = [this.getDeleteGraphicPopup()];
      if (annotation.getCustomData(GraphicZoneCustomData.confirmed) !== 'true') {
        popupButtons.push(this.getConfirmSearchPopup(this.source.instance, DocumentTypes.source));
      }
      this.source.instance.UI.annotationPopup.update(popupButtons);
      // get the matching source zone by id
      const mirrorAnnotationTarget = this.target?.getAnnotationById(annotation.Id);

      // jump and select matching target annotation if its not already selected by loop
      if (mirrorAnnotationTarget && !this.target?.annotationManager.isAnnotationSelected(mirrorAnnotationTarget)) {
        this.target?.instance.Core.annotationManager.jumpToAnnotation(mirrorAnnotationTarget);
        setTimeout(() => {
          this.target?.instance.Core.annotationManager.selectAnnotation(mirrorAnnotationTarget);
        }, 50);
      }
    } else {
      if (annotation.getCustomData(GraphicZoneCustomData.confirmed) !== 'true') {
        let DropDownMenuDirection = '';

        // PDFTron sometimes throws an error when trying to get the position of newly created annotations. This will
        // now default to a direction if it does.
        try {
          const annotationPosition = getPositionOfAnnotationRelativeToDocument(annotation, DocumentTypes.source);
          if (!annotationPosition) return;

          const viewElement = PDFTronManager.getPDFInstance(
            DocumentTypes.target,
          )?.Core.documentViewer.getScrollViewElement();
          if (!viewElement) return;

          const DROPDOWNMENU_MAX_HEIGHT = 75; // DropdownMenu height.
          const ANNOTATION_POPUP_HEIGHT = 40; // Annotation Popup maxHeight

          const { clientHeight } = viewElement;
          const { y, heightOffset } = annotationPosition;

          const annotationSpaceLeft = clientHeight - (y + heightOffset);
          if (
            annotationSpaceLeft < DROPDOWNMENU_MAX_HEIGHT + ANNOTATION_POPUP_HEIGHT &&
            annotationSpaceLeft > ANNOTATION_POPUP_HEIGHT
          ) {
            DropDownMenuDirection = '-103px';
          }
        } catch {
          DropDownMenuDirection = '8px';
        }

        this.target?.instance.UI.annotationPopup.update([
          this.getEditShiftedPopup(this.target.instance, DropDownMenuDirection),
          this.getSeparator(),
          this.getDeleteGraphicPopup(),
          this.getConfirmGraphicPopup(this.target.instance, DocumentTypes.target),
        ]);
      } else {
        this.target?.instance.UI.annotationPopup.update([]);
      }

      // get the matching source zone by id
      const mirrorAnnotationSource = this.source?.getAnnotationById(annotation.Id);

      // jump and select matching source annotation if its not already selected by loop
      if (mirrorAnnotationSource && !this.source?.annotationManager.isAnnotationSelected(mirrorAnnotationSource)) {
        this.source?.instance.Core.annotationManager.jumpToAnnotation(mirrorAnnotationSource);
        setTimeout(() => {
          this.source?.instance.Core.annotationManager.selectAnnotation(mirrorAnnotationSource);
        }, 50);
      }
    }
  }

  // called from saga
  graphicZoneAnnotationChanged(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    if (action === 'add') {
      if (documentType === DocumentTypes.source) {
        this.source?.annotationManager.selectAnnotation(annotation);
        // deselect all target annotations so we don't delete them afterwards.
        this.target?.annotationManager.deselectAllAnnotations();
        const unconfirmedGraphicZones = this.getUnconfirmedGraphicZones();
        const targetUnconfirmedZones = this.getUnconfirmedTargetGraphicZones();
        if (unconfirmedGraphicZones.length > 1 || targetUnconfirmedZones.length > 0) {
          store.dispatch(inspection.actions.setUnconfirmGraphicZoneError(true));
          this.deleteGraphicZones();
          this.source?.instance.Core.annotationManager.deleteAnnotation(annotation);
          this.selectUnconfirmedGraphicZone();
        }
      } else {
        this.target?.annotationManager.jumpToAnnotation(annotation);
      }

      this.source?.instance.Core.annotationManager?.selectAnnotation(annotation);
    }
  }

  getDeleteGraphicPopup() {
    return {
      type: 'actionButton',
      img: '/icons/zoneDelete.svg',
      title: 'Delete',
      onClick: () => {
        this.deleteGraphicZones();
        this.handlingDocumentHighlight();
      },
    };
  }

  findBox(ar: any[]) {
    const start = ar[0].pt;

    let minX = start.x;
    let maxX = start.x;
    let minY = start.y;
    let maxY = start.y;

    for (let i = 1; i < ar.length; i++) {
      const check = ar[i].pt;

      minX = Math.min(minX, check.x);
      maxX = Math.max(maxX, check.x);
      minY = Math.min(minY, check.y);
      maxY = Math.max(maxY, check.y);
    }

    return {
      x1: minX,
      y1: minY,
      x2: maxX,
      y2: maxY,
      w: maxX - minX,
      h: maxY - minY,
    };
  }

  // Related to - VE-2793 - Confirm button - New Panel menu - (To Edit)
  getConfirmGraphicPopup(instance: WebViewerInstance, documentType: DocumentTypes) {
    return {
      type: 'actionButton',
      img: '/icons/check-icon.svg',
      title: 'Confirm',
      onClick: () => {
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        const sourceAnnoManager = this.source?.annotationManager;
        const targetAnnoManager = this.target?.annotationManager;
        if (documentType === DocumentTypes.source) {
          sourceAnnoManager?.deselectAnnotation(selectedAnnotation);
          if (this.target) {
            this.duplicateGraphicZone(selectedAnnotation, this.target.instance);
          }
        } else {
          store.dispatch(inspection.actions.increaseGraphicZoneId());
          this.annotationMixin.updateInputAnnotations();
          targetAnnoManager?.deselectAnnotation(selectedAnnotation);
        }
        selectedAnnotation.setCustomData(GraphicZoneCustomData.confirmed, 'true');
        selectedAnnotation.ReadOnly = true;
        this.handlingDocumentHighlight();
      },
    };
  }

  static distance = (a: any, t: any) => Math.abs(t - a);

  // Related to - VE-2793 - Confirm button - New Panel menu - (To Edit)
  getConfirmSearchPopup(instance: WebViewerInstance, documentType: DocumentTypes) {
    return {
      type: 'actionButton',
      img: '/icons/check-icon.svg',
      title: 'Confirm',
      onClick: async () => {
        if (!this.source || !this.target) return;

        // Lock UI while searching
        store.dispatch(inspection.actions.setSearchingForZone(true));
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];

        const sourceAnnoManager = this.source?.annotationManager;
        const targetAnnoManager = this.target?.annotationManager;

        selectedAnnotation.NoResize = true;
        selectedAnnotation.NoMove = true;
        sourceAnnoManager?.updateAnnotation(selectedAnnotation);

        let state = store.getState();
        const autoMatchGraphic = getAutoMatchGraphic(state);

        if (documentType === DocumentTypes.source) {
          if (this.target && this.source) {
            try {
              // USING CTF MATCHING
              const zoneRect = selectedAnnotation.getRect();
              const crop = {
                boundingBox: { top: zoneRect.y1, left: zoneRect.x1, bottom: zoneRect.y2, right: zoneRect.x2 },
                pageNumber: selectedAnnotation.PageNumber,
              };

              let bestMatch: CtfMatchLocation | undefined;
              try {
                const response = await requestsStore.dispatchRequest(
                  startMatching(state.inspection.inspectionId, crop),
                );
                if (!response.data) {
                  throw new Error('error in start matching');
                }
                const matchId = response.data.id;

                bestMatch = await new Promise<CtfMatchLocation | undefined>((res) => {
                  const intervalId = window.setInterval(() => {
                    try {
                      const stateUpdate = store.getState();
                      if (!stateUpdate.inspection.searchingForGraphicZone) {
                        window.clearInterval(intervalId);
                        throw new Error('cancelled');
                      }
                      requestsStore
                        .dispatchRequest(fetchMatchInspection(matchId))
                        .then((response) => {
                          if (response.error) {
                            throw new Error('error in start matching');
                          }
                          if (response.data?.status === InspectionStatuses.error) {
                            throw new Error('error in matching');
                          }
                          if (response.data?.status === InspectionStatuses.completed) {
                            window.clearInterval(intervalId);

                            const bestMatch = response.data.bestMatch || undefined;
                            // eslint-disable-next-line
                            res(bestMatch);
                          }
                        })
                        .catch((_err) => {
                          window.clearInterval(intervalId);
                          store.dispatch(inspection.actions.setSearchingForZone(false));
                        });
                    } catch {
                      window.clearInterval(intervalId);
                    }
                  }, 1000);
                });
              } catch {
                store.dispatch(inspection.actions.setSearchingForZone(false));
                return;
              }

              // we need to deselect the annotation so that when it is reselected, it is updated with the correct UI
              sourceAnnoManager?.deselectAnnotation(selectedAnnotation);
              if (bestMatch) {
                // case #1: ctf graphics gives successful match
                // graphics matches are ordered from best match to worst
                const duplicateAnnotation = this.duplicateFoundGraphicZone(
                  selectedAnnotation,
                  this.target.instance,
                  bestMatch,
                );
                selectedAnnotation.setCustomData(GraphicZoneCustomData.autoGraphicsAnnotations, 'true');
                store.dispatch(
                  inspection.actions.setAutoMatchGraphic({
                    autoMatchPass: autoMatchGraphic.autoMatchPass ? autoMatchGraphic.autoMatchPass + 1 : 1,
                  }),
                );
                selectedAnnotation.setCustomData(GraphicZoneCustomData.shifted, 'true'); // we set the shifted flag for automatched graphics to disable auto-detection/auto-discord
                duplicateAnnotation.setCustomData(GraphicZoneCustomData.shifted, 'true'); // we set the shifted flag for automatched graphics to disable auto-detection/auto-discord
              } else {
                // no matches found from ctf
                this.duplicateGraphicZone(selectedAnnotation, this.target.instance, 1);
                store.dispatch(
                  inspection.actions.setAutoMatchGraphic({
                    autoMatchFail: autoMatchGraphic.autoMatchFail ? autoMatchGraphic.autoMatchFail + 1 : 1,
                  }),
                );
              }
            } catch {
              // If there is an error, revert back to default behavoir
              this.duplicateGraphicZone(selectedAnnotation, this.target.instance);
              const duplicateAnnotation = this.target.instance.Core.annotationManager.getAnnotationById(
                selectedAnnotation.Id,
              );
              selectedAnnotation.setCustomData(GraphicZoneCustomData.shifted, 'true'); // we set the shifted flag to disable auto-detection for failed zones
              duplicateAnnotation.setCustomData(GraphicZoneCustomData.shifted, 'true'); // we set the shifted flag to disable auto-detection for failed zones
            }
          }
        } else {
          store.dispatch(inspection.actions.increaseGraphicZoneId());
          this.annotationMixin.updateInputAnnotations();
          targetAnnoManager?.deselectAnnotation(selectedAnnotation);
        }
        selectedAnnotation.setCustomData(GraphicZoneCustomData.confirmed, 'true');
        selectedAnnotation.ReadOnly = true;
        this.handlingDocumentHighlight();

        store.dispatch(inspection.actions.setSearchingForZone(false));
      },
    };
  }

  containsGraphicAnnotation() {
    return (
      this.source?.annotationManager.getAnnotationsList().find((value: { ToolName: string }) => {
        return value.ToolName === 'GraphicCreateTool';
      }) ||
      this.target?.annotationManager.getAnnotationsList().find((value: { ToolName: string }) => {
        return value.ToolName === 'GraphicCreateTool';
      })
    );
  }

  GraphicDifferenceFocused(differences: Difference[]): void {
    if (!differences || differences.length === 0) {
      return;
    }

    // Skip the animation for embedded images with confidenceLevel 'None' since they're treated as deletions
    const matchDetails = differences[0].embeddedImageDetails?.matchDetails?.[0];
    const isNoMatchEmbeddedImage =
      differences[0].type === DifferenceTypeNames.EmbeddedImage &&
      matchDetails?.confidenceLevel === 'None' &&
      !differences[0].embeddedImageDetails?.userLinkedMatch;

    if (isNoMatchEmbeddedImage) {
      // Clear any existing match index to prevent flashing with wrong images
      store.dispatch(app.actions.setFocusedMatchIndex(-1));
      return;
    }

    if (getGraphicMode(store.getState()) && differences[0].matchIndex !== undefined) {
      store.dispatch(app.actions.setFocusedMatchIndex(differences[0].matchIndex)); // Triggers GraphicMatchFocused for the associated Match
    }
  }

  GraphicMatchFocused(match: Match | CtfMatchResult): void {
    const state = store.getState();
    const graphicMode = getGraphicMode(state);
    if (graphicMode) {
      this.resetAnimation();
      // Only animate if there's actually a focused difference (a card is selected)
      const focusedDifference = getFocusedDifference(state);
      if (focusedDifference && focusedDifference.id !== '-1') {
        this.animateMatch(match);
      }
    }
  }

  getDrawMatchPopup(differenceId: string, deletionTarget: DocumentTypes) {
    return createAnnotationPopupButton({
      label: 'Draw a match',
      iconSrc: '/icons/drawComparison.svg',
      onClick: () => {
        this.markupMode = MarkupMode.EMBEDDED_IMAGE_MATCH;
        this.originalMarkupId = differenceId;
        this.comparisonSourceDocType = deletionTarget;

        const wrapper = deletionTarget === DocumentTypes.source ? this.source : this.target;
        wrapper?.addDocumentHighlight();

        store.dispatch(inspection.actions.setSelectedTool({ tool: PDFTronTools.MARKUP }));
        wrapper?.instance.UI.setToolMode(PDFTronTools.MARKUP);

        const sourceAnnotManager = PDFAnnotationManager.getInstance(DocumentTypes.source);
        const targetAnnotManager = PDFAnnotationManager.getInstance(DocumentTypes.target);
        sourceAnnotManager?.deselectAllAnnotations();
        targetAnnotManager?.deselectAllAnnotations();
      },
    });
  }
}

export default GraphicZone;
